import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ProductsConfigService } from '@approot/services';
import { Utilities } from '@approot/utilities';
import optionsJson from './porfolios-dropdown-options.json';
@Component({
  selector: 'app-options-portfolio',
  templateUrl: './options-portfolio.component.html',
  styleUrls: ['./options-portfolio.component.scss']
})
export class OptionsPortfolioComponent implements OnInit {
  @Input() legForm: FormGroup;
  @Output() optionsPortfolioOpenChanged = new EventEmitter();
  @Output() requestLatestSpot = new EventEmitter<{
    control: FormControl;
    cross: string;
  }>();

  portfolioOptionsDropdown = optionsJson;
  currencyPairs: string[] = [];
  currencies: string[] = [];
  portfolioGroup: FormGroup;
  selectedOptionIndex: number = null;
  constructor(private productsConfigService: ProductsConfigService) {}

  ngOnInit() {
    this.currencyPairs =
      this.productsConfigService.cutOffTimesAndFixingSources.currencyPairInfo.map(
        pairInfo => pairInfo.currencyPair
      );

    this.portfolioGroup = new FormGroup({
      options: new FormArray([])
    });

    // we need to make a copy of the portfolio, so edits don't affect the leg
    this.legForm.get('optionsPortfolio').value.map(option => {
      (this.portfolioGroup.get('options') as FormArray).push(
        this.makeOptionGroup(option)
      );
    });
  }

  makeOptionGroup(rowData?: any) {
    const optionRow = Utilities.generateOption(rowData);

    optionRow.get('currencyPair').valueChanges.subscribe(cross => {
      optionRow.get('currency').setValue('');
      if (
        optionRow.get('currencyPair').value &&
        !isNaN(optionRow.get('strike').value)
      ) {
        optionRow
          .get('strike')
          .setValue(
            this.productsConfigService.crossDecimalFormatter(
              optionRow.get('strike').value,
              cross
            )
          );
      }
    });

    optionRow.get('productType').valueChanges.subscribe(productType => {
      Utilities.optionPortfolioDisableOrEnableBarrier(productType, optionRow);
    });

    return optionRow;
  }

  strikeBlur(item: AbstractControl) {
    // when bluring a text box convert the attribute changed into its formatted version that depends on the
    // cross if possible
    if (item.get('currencyPair').value) {
      item
        .get('strike')
        .setValue(
          this.productsConfigService.crossDecimalFormatter(
            item.get('strike').value,
            item.get('currencyPair').value
          )
        );
    }
  }

  get optionsArray() {
    return (this.portfolioGroup.get('options') as FormArray).controls;
  }

  addNewOption() {
    (this.portfolioGroup.get('options') as FormArray).push(
      this.makeOptionGroup()
    );
  }

  deleteOption() {
    (this.portfolioGroup.get('options') as FormArray).removeAt(
      this.selectedOptionIndex
    );
    (this.portfolioGroup.get('options') as FormArray).markAsDirty(); // mark array as dirty
    this.selectedOptionIndex = null; // no row is currently selected
  }

  saveChanges() {
    if ((this.portfolioGroup.get('options') as FormArray).dirty) {
      (this.legForm.get('optionsPortfolio') as FormArray).clear();
      (this.portfolioGroup.get('options') as FormArray).controls.forEach(
        (optionControl: FormControl, index: number) => {
          // we need to format the notional into the correct format
          const notional = optionControl.get('notional');
          notional.setValidators([Validators.required]);
          notional.setValue(notional.value.replace(/,/g, ''), {
            emitEvent: false
          });

          this.addSpot(optionControl.value.currencyPair);

          (this.legForm.get('optionsPortfolio') as FormArray).setControl(
            index,
            optionControl
          );
        }
      );
    }
    this.removeUnusedSpots(); // remove spots no longer needed
    this.closeModal();
  }

  addSpot(optionsPortfolioCurrencyPair: string) {
    // add spot into the optionsPortfolioSpots
    const optionPortfolioSpotsCurrencyPair: string[] = (
      this.legForm.get('optionsPortfolioSpots') as FormArray
    ).value.map(spot => Object.keys(spot)[0]); // get all the currency pairs from optionsPortfolioSpots

    if (
      !optionPortfolioSpotsCurrencyPair.includes(optionsPortfolioCurrencyPair)
    ) {
      // if currency pair is not in spots, add it
      const spotControl = new FormControl('', Validators.required);

      (this.legForm.get('optionsPortfolioSpots') as FormArray).push(
        new FormGroup({
          [optionsPortfolioCurrencyPair]: spotControl
        })
      );
      this.requestSpot(spotControl, optionsPortfolioCurrencyPair); // get the latest spot
    }
  }

  removeUnusedSpots() {
    // remove currencyPair spots that are no longer in options portfolio

    // all optionsPortfolioSpots currency pairs
    const optionPortfolioSpotsCurrencyPair: string[] = (
      this.legForm.get('optionsPortfolioSpots') as FormArray
    ).value.map(spot => Object.keys(spot)[0]);

    // check if any currency pairs in the spots is in the the options portfolio if not remove it
    optionPortfolioSpotsCurrencyPair.forEach(spotCurrencyPair => {
      const isCurrencyPairInOptionsPortfolio = (
        this.legForm.get('optionsPortfolio') as FormArray
      ).value.find(
        optionsPortfolio => optionsPortfolio.currencyPair === spotCurrencyPair
      );
      if (!isCurrencyPairInOptionsPortfolio) {
        // if the currency pair is not in the options portfolio remove the form group
        const indexOfSpot = (
          this.legForm.get('optionsPortfolioSpots') as FormArray
        ).value.findIndex(spot => Object.keys(spot)[0] === spotCurrencyPair); // get index of what we are removing

        (this.legForm.get('optionsPortfolioSpots') as FormArray).removeAt(
          indexOfSpot
        );
      }
    });
  }

  currencyFocus(option: AbstractControl) {
    if (option.get('currencyPair').value) {
      this.currencies = [
        option.get('currencyPair').value.substring(0, 3),
        option.get('currencyPair').value.substring(3, 6)
      ];
    }
  }

  requestSpot(formControl: FormControl, cross: string) {
    this.requestLatestSpot.emit({ control: formControl, cross });
  }

  closeModal() {
    this.optionsPortfolioOpenChanged.emit(false);
  }
}
